// Typed client for /api/AiAssistant/* — the AI assistant chat surface.
// The browser only exchanges chat messages; the LLM call and tool-use loop
// run server-side.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

/** Result of one chat turn. ok=false carries an error to render inline. */
export interface AiChatResult {
  ok: boolean;
  error: string | null;
  conversationId: string;
  reply: string;
  toolsUsed: string[];
}

export interface ChatRequest {
  conversationId?: string | null;
  message: string;
  context?: string;        // 'general' | 'paper' | 'live' | 'strategy'
}

/**
 * One event from the streaming chat endpoint (SSE).
 *  - kind 'tool'    — a tool started/finished (`tool`, `toolStatus`)
 *  - kind 'message' — the final assistant text (`text`)
 *  - kind 'done'    — turn finished (`conversationId`, `toolsUsed`)
 *  - kind 'error'   — the assistant failed (`text`)
 */
export interface AiChatEvent {
  kind: 'tool' | 'message' | 'done' | 'error';
  tool?: string;
  toolStatus?: 'started' | 'finished';
  text?: string;
  conversationId?: string;
  toolsUsed?: string[];
}

export interface ChatTurn {
  role: string;            // 'User' | 'Assistant'
  text: string;
  createdAt: string;
  /** If this turn came from a firing of an AiSchedule, the run id —
   *  lets the UI badge "scheduled run · HH:mm" on synthetic turns. */
  scheduledRunId: string | null;
}

export interface Conversation {
  id: string;
  context: string;
  title: string | null;
  turns: ChatTurn[];
  archivedAt: string | null;
}

export interface ConversationSummary {
  id: string;
  title: string | null;
  context: string;
  updatedAt: string;
  archivedAt: string | null;
}

/** One schedule attached to a conversation — recurring loop or one-shot run. */
export interface AiSchedule {
  id: string;
  conversationId: string;
  name: string;
  kind: 'Recurring' | 'OneShot';
  prompt: string;
  intervalMinutes: number | null;
  runAtUtc: string | null;
  enabled: boolean;
  nextRunAtUtc: string | null;
  lastRunAtUtc: string | null;
  runCount: number;
  errorCount: number;
  createdAt: string;
}

export interface AiScheduledRun {
  id: string;
  scheduleId: string;
  conversationId: string;
  startedAtUtc: string;
  endedAtUtc: string | null;
  ok: boolean;
  error: string | null;
  reply: string;
  toolsUsed: string[];
}

/** A trade the assistant wants to make (or has made). */
export interface TradeProposal {
  id: string;
  kind: string;            // 'Open' | 'Close'
  symbol: string;
  side: string;            // 'buy' | 'sell' | ''
  quantity: number;
  rationale: string | null;
  status: string;          // 'Pending' | 'Rejected' | 'Executed' | 'Failed' | 'Executing'
  resultMessage: string | null;
  createdAt: string;
  decidedAt: string | null;
  /** Conversation that produced this proposal — UI links the trade card
   *  back to its origin thread. */
  conversationId: string | null;
  /** Set when the proposal was raised by a scheduled run — UI badges
   *  "Triggered by loop". */
  scheduleId: string | null;
  scheduleRunId: string | null;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(await res.text() || `Request failed (${res.status})`);
  return res.json() as Promise<T>;
}

async function post<T>(path: string): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`, { method: 'POST' });
  if (!res.ok) throw new Error(await res.text() || `Request failed (${res.status})`);
  return res.json() as Promise<T>;
}

async function postNoContent(path: string): Promise<void> {
  const res = await apiFetch(`${API_BASE}${path}`, { method: 'POST' });
  if (!res.ok) throw new Error((await res.text()) || `Request failed (${res.status})`);
}

async function del(path: string): Promise<void> {
  const res = await apiFetch(`${API_BASE}${path}`, { method: 'DELETE' });
  if (!res.ok) throw new Error((await res.text()) || `Request failed (${res.status})`);
}

export const aiAssistantService = {
  /** Send a message; the backend runs the full agent loop and returns the reply. */
  chat: async (req: ChatRequest): Promise<AiChatResult> => {
    const res = await apiFetch(`${API_BASE}/api/AiAssistant/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!res.ok) throw new Error(await res.text() || `Request failed (${res.status})`);
    return res.json() as Promise<AiChatResult>;
  },

  /**
   * Send a message and stream the agent's progress (SSE). `onEvent` fires for
   * each tool start/finish, the final message, and the closing done/error.
   * Resolves when the stream ends.
   */
  chatStream: async (
    req: ChatRequest,
    onEvent: (ev: AiChatEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    const res = await apiFetch(`${API_BASE}/api/AiAssistant/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
      signal,
    });
    if (!res.ok || !res.body) {
      throw new Error((await res.text()) || `Request failed (${res.status})`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line.
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
        const json = dataLine?.slice(5).trim();
        if (!json) continue;
        try {
          onEvent(JSON.parse(json) as AiChatEvent);
        } catch {
          /* ignore a malformed frame */
        }
      }
    }
  },

  /** Load a past conversation's transcript. */
  getConversation: (id: string) =>
    getJson<Conversation>(`/api/AiAssistant/conversations/${encodeURIComponent(id)}`),

  /** List the caller's conversations. `archived=true` returns the archive bin
   *  instead of the active list. */
  listConversations: (archived = false, limit = 50) =>
    getJson<ConversationSummary[]>(
      `/api/AiAssistant/conversations?archived=${archived}&limit=${limit}`),

  /** Archive (hide from default list + auto-disable schedules). */
  archiveConversation: (id: string) =>
    postNoContent(`/api/AiAssistant/conversations/${encodeURIComponent(id)}/archive`),

  /** Un-archive. Does NOT re-enable previously-disabled schedules — the
   *  user must re-enable each one explicitly. */
  unarchiveConversation: (id: string) =>
    postNoContent(`/api/AiAssistant/conversations/${encodeURIComponent(id)}/unarchive`),

  // ── schedules ─────────────────────────────────────────────────────

  /** Every schedule the caller owns — active + disabled, recurring + one-shot. */
  listSchedules: () => getJson<AiSchedule[]>('/api/AiAssistant/schedules'),

  /** Schedules attached to one conversation — what the conversation header
   *  schedules row shows. */
  listConversationSchedules: (conversationId: string) =>
    getJson<AiSchedule[]>(
      `/api/AiAssistant/conversations/${encodeURIComponent(conversationId)}/schedules`),

  disableSchedule: (id: string) =>
    post<AiSchedule>(`/api/AiAssistant/schedules/${encodeURIComponent(id)}/disable`),

  enableSchedule: (id: string) =>
    post<AiSchedule>(`/api/AiAssistant/schedules/${encodeURIComponent(id)}/enable`),

  deleteSchedule: (id: string) =>
    del(`/api/AiAssistant/schedules/${encodeURIComponent(id)}`),

  listScheduleRuns: (id: string, limit = 20) =>
    getJson<AiScheduledRun[]>(
      `/api/AiAssistant/schedules/${encodeURIComponent(id)}/runs?limit=${limit}`),

  /** Pending trade proposals awaiting the user's decision. */
  listProposals: () => getJson<TradeProposal[]>('/api/AiAssistant/proposals'),

  /** Approve a proposal — sends the order to the broker. */
  approveProposal: (id: string) =>
    post<TradeProposal>(`/api/AiAssistant/proposals/${encodeURIComponent(id)}/approve`),

  /** Reject a proposal — it will not be placed. */
  rejectProposal: (id: string) =>
    post<TradeProposal>(`/api/AiAssistant/proposals/${encodeURIComponent(id)}/reject`),
};
